import { useState } from "react";

/**
 * Phase 29.C — Capture-this-moment pill.
 *
 * Surfaces alongside `ShareNudgePill` when the share-worthiness classifier
 * fires with a high-energy kind (funny/achievement/milestone/wholesome). Goal:
 * encourage REAL human-moment capture (selfie / room shot / candid), not just
 * forwarding text.
 *
 * Two CTAs:
 *   📸 Snap a fresh one  → opens camera with the prompt as caption hint
 *   🖼️ Use current wallpaper → opens the existing Snap share confirm card
 *      pre-filled with the current wallpaper + Aura-drafted caption
 *
 * Dismiss → reports outcome so backoff applies the same way as ShareNudgePill.
 *
 * Props
 * -----
 *  - captureMoment: { kind, prompt }
 *  - onCapture: () => void       (parent triggers openCamera + closes the pill)
 *  - onUseWallpaper: () => void  (parent stages a share_to_snap pending action)
 *  - onDismissed: () => void
 */
export function CaptureMomentPill({ captureMoment, onCapture, onUseWallpaper, onDismissed }) {
  const [dismissed, setDismissed] = useState(false);
  if (!captureMoment || dismissed) return null;
  const { prompt } = captureMoment;

  const handleDismiss = (e) => {
    e.stopPropagation();
    setDismissed(true);
    onDismissed?.();
  };

  return (
    <div className="capture-moment-pill" data-testid="capture-moment-pill">
      <div className="capture-moment-row">
        <span className="capture-moment-icon" aria-hidden>📸</span>
        <span className="capture-moment-text">{prompt}</span>
        <span
          className="nudge-dismiss"
          data-testid="capture-moment-dismiss"
          onClick={handleDismiss}
          role="button"
          aria-label="Dismiss capture suggestion"
        >×</span>
      </div>
      <div className="capture-moment-actions">
        <button
          type="button"
          className="capture-moment-btn capture-moment-btn-primary"
          data-testid="capture-moment-snap-btn"
          onClick={() => {
            setDismissed(true);
            onCapture?.();
          }}
        >
          📸 Snap a fresh one
        </button>
        <button
          type="button"
          className="capture-moment-btn capture-moment-btn-secondary"
          data-testid="capture-moment-wallpaper-btn"
          onClick={() => {
            setDismissed(true);
            onUseWallpaper?.();
          }}
        >
          🖼️ Use this wallpaper
        </button>
      </div>
    </div>
  );
}
